<template>
    <div>
        <div v-if="!courseCodes">
            <e-field v-if="media && media.length > 0" class="attachments" :class="{ inline }" :label="label">
                <div class="columns is-multiline is-gapless is-1 is-full">
                    <div v-for="(item, index) in media" :key="item" class="column is-width-full mb-4">
                        <download-file-chip
                            :key="index"
                            :file="item"
                            :can-close="closable"
                            @delete="$emit('delete', { item, index })"
                        />
                    </div>
                </div>
            </e-field>

            <e-message
                v-if="showTurnitinStatusMessages && showTurnitinExperience"
                type="is-info"
                size="is-small"
            >
                Grades submitted in Turnitin may take a few moments to sync with the Edverum gradebook.
            </e-message>

            <span
                v-if="showTurnitinSubmission && showTurnitinExperience"
                class="is-flex is-align-items-center is-flex-wrap-wrap is-flex-direction-row-tablet is-flex-direction-column-mobile is-justify-content-flex-start-tablet is-justify-content-center-mobile"
            >
                <e-icon icon="file-upload" class="is-success mr-2" />
                File Uploaded in
                <span class="has-text-primary ml-1">Turnitin</span>
                <e-button
                    class="button is-primary ml-5 mt-2-mobile"
                    @click="onViewAttachment"
                >View Assignment</e-button>
            </span>

            <turnitin-review-and-grade-drawer
                v-if="showTurnitinSubmission && showTurnitinExperience && ltiResourceLinkId"
                v-model:active="showReviewDrawer"
                :lti-resource-link-id="ltiResourceLinkId"
                :for-user-id="forUserId"
                :line-item-id="lineItemId"
            />
        </div>
        <div v-else>
            <div>
                <meta-panel>
                    <meta-category
                        icon="paperclip"
                        title="Attachments"
                        body-fallback="No attachments for this activity."
                        :can-collapse="false"
                    >
                        <file-chip v-for="(item, index) in attachments" :key="index" :file="item" />

                        <div v-if="objective" class="file-chip objective-chip">
                            <e-tag size="is-medium" class="objective-tag">
                                <span v-if="!$isMobile" class="objective-title-text">{{ truncatedObjectiveTitle }}</span>
                                <span v-else class="objective-title-text">{{ truncatedObjectiveTitleMobile }}</span>
                                <button
                                    class="tag has-text-weight-bold is-clickable tw:bg-transparent tw:bg-[var(--p-tag-primary-background)]! objective-download-btn"
                                    aria-label="Download objectives"
                                    @click.prevent="$emit('downloadObjectives', true)"
                                >
                                    <e-icon icon="download-alt" size="is-small" type="is-primary" />
                                </button>
                            </e-tag>
                        </div>
                    </meta-category>
                </meta-panel>
            </div>
        </div>
    </div>
</template>

<script>
import MetaCategory from '@/components/meta-panel/MetaCategory.vue';
import MetaPanel from '@/components/meta-panel/MetaPanel.vue';
import TurnitinReviewAndGradeDrawer from '@/components/turnitin/TurnitinReviewAndGradeDrawer.vue';
import DownloadFileChip from '@/components/utilities/DownloadFileChip.vue';
import FileChip from '@/components/utilities/FileChip.vue';
import CourseCodes from '@/models/CourseCodes.js';

export default {
    name: 'AttachmentsComponent',

    components: { DownloadFileChip, FileChip, MetaCategory, MetaPanel, TurnitinReviewAndGradeDrawer },

    props: {
        media: {
            type: Array,
            required: false,
            default: null,
        },

        label: {
            type: String,
            required: false,
            default: 'Attachments',
        },

        inline: {
            type: Boolean,
            default: false,
        },

        deletable: {
            type: Boolean,
            default: true,
        },

        courseCodes: {
            type: Array,
            required: false,
            default: null,
        },

        objective: {
            type: Object,
            required: false,
            default: null,
        },

        isTurnItSubmission: {
            type: Boolean,
            default: false,
        },

        ltiResourceLinkId: {
            type: Number,
            required: false,
            default: null,
        },
        forUserId: {
            type: [Number, String],
            required: false,
            default: null,
        },
        lineItemId: {
            type: [Number, String],
            required: false,
            default: null,
        },

        isTurnitinEnabled: {
            type: Boolean,
            default: false,
        },

        showTurnitinStatusMessages: {
            type: Boolean,
            default: false,
        },

        showTurnitinSubmission: {
            type: Boolean,
            default: true,
        },
    },

    emits: ['delete', 'downloadObjectives'],

    data() {
        return {
            isOpen: 0,
            isLoading: false,
            attachments: [],
            showReviewDrawer: false,
        };
    },

    computed: {
        /**
         * Persisted Turnitin metadata should not alter UI when the integration is off.
         *
         * @returns {boolean}
         */
        showTurnitinExperience() {
            return this.isTurnItSubmission && this.isTurnitinEnabled;
        },

        /**
         * @returns {boolean}
         */
        closable() {
            return this.deletable && this.$options.emits.includes('delete');
        },

        /**
         * Truncate the objective title when it exceeds the character limit to
         * keep the Attachments tag from ballooning in narrow layouts.
         *
         * @returns {string}
         */
        truncatedObjectiveTitle() {
            const maxLength = 40;
            const title = this.objective?.title || 'Clinical Objectives';

            if (title.length <= maxLength) {
                return title;
            }

            return `${title.slice(0, maxLength).trimEnd()}...`;
        },

        truncatedObjectiveTitleMobile() {
            const maxLength = 20;
            const title = this.objective?.title || 'Clinical Objectives';

            if (title.length <= maxLength) {
                return title;
            }

            return `${title.slice(0, maxLength).trimEnd()}...`;
        },
    },

    mounted() {
        if (this.courseCodes && this.courseCodes.length === 1) {
            this.getAttachments();
        }
    },

    methods: {
        async getAttachments() {
            this.isLoading = true;
            const { response } = await CourseCodes.fetchAttachments(this.courseCodes[0].id, 'objective');

            this.attachments = response.data.data[0].attachments;

            return (this.isLoading = false);
        },

        onViewAttachment() {
            this.showReviewDrawer = true;
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.objective-chip {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
}

.objective-chip .objective-tag.p-tag {
    display: inline-flex;
    align-items: flex-start;
    max-width: 100%;
    min-width: 0;
    width: 100%;
}

.objective-title-text {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
    line-height: 1.25;
}

.objective-download-btn {
    flex: 0 0 auto;
    align-self: center;
}

.attachments {
    & > div {
        align-items: flex-start;
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
    }

    &.inline > div {
        flex-direction: row;
        margin-bottom: -$tag-gap;
    }
}
</style>
